// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Which currency this browser sees prices in (01 §6).
//
// **A display preference that the server re-decides.** The stored value here chooses which column
// of `PRICING` the paywall renders and travels to `billing.checkout` as a *request*; what the
// server does with it is look up one of four Price IDs it holds. That distinction is the whole
// security property: a browser that could send an amount could choose what it pays, and a browser
// that sends the word `CAD` is choosing between two rows in somebody else's table.
//
// **Separate from the language, deliberately.** A French-speaking American is a real person and so
// is an English-speaking Canadian, and a product that inferred one from the other would be wrong
// about both of them. What the language *does* decide is how the amount is written — `CA$121.00`
// or `121,00 $ CA` — which is `Intl`'s business and comes from the locale.
//
// **Defaulted from the browser's region, then never guessed again.** A first visit from a Canadian
// browser opens on Canadian dollars because that is far more likely to be right than not; the
// moment a player chooses, the choice outranks the guess and keeps outranking it.

import { readLocal, safeStorage, writeLocal, type StorageLike } from '../../storage/local'
import { stampPreference } from '../../storage/prefStamps'
import { DEFAULT_CURRENCY, isCurrency, type Currency } from '@src/ui/pricingCatalog'

/** Where the choice is kept. One key, so it cannot be spelled two ways. */
export const CURRENCY_KEY = 'hcc.pinball.currency'

/**
 * What this browser's region suggests, before anybody has chosen.
 *
 * Canadian region or Canadian French means Canadian dollars; everything else means US dollars.
 * Deliberately crude — there are two currencies and this is a first guess that a single tap
 * overrides, so anything more elaborate would be more ways to be confidently wrong.
 */
export function browserCurrency(
  navigatorLike: { languages?: readonly string[]; language?: string } | undefined =
    (globalThis as { navigator?: { languages?: readonly string[]; language?: string } }).navigator,
): Currency {
  const tags = navigatorLike?.languages ?? (navigatorLike?.language === undefined ? [] : [navigatorLike.language])
  for (const tag of tags) {
    if (typeof tag === 'string' && tag.toLowerCase().endsWith('-ca')) return 'CAD'
  }
  return DEFAULT_CURRENCY
}

export function readCurrency(storage: StorageLike | undefined = safeStorage()): Currency {
  const stored = readLocal(storage, CURRENCY_KEY)
  return isCurrency(stored) ? stored : browserCurrency()
}

export function writeCurrency(
  currency: Currency,
  storage: StorageLike | undefined = safeStorage(),
): void {
  writeLocal(storage, CURRENCY_KEY, currency)
  stampPreference('currency', storage)
}
